import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUp, History, Loader2, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";
import { CoinMark } from "@/components/brand/Logo";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { useHousehold } from "@/context/HouseholdContext";
import { API, api, formatApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

const SUGGESTIONS = [
  "Combien me reste-t-il sur mes budgets ce mois-ci ?",
  "Quelles garanties expirent bientôt ?",
  "Quels entretiens arrivent dans les prochaines semaines ?",
  "Où puis-je faire des économies ?",
  "Ma consommation d'électricité est-elle en hausse ?",
  "Quels équipements sont couverts par mon assurance ?",
];

const Bubble = ({ m }) => (
  <div className={cn("flex gap-3", m.role === "user" && "justify-end")} data-testid={`assistant-msg-${m.role}`}>
    {m.role === "assistant" && <CoinMark size={28} className="mt-1 shrink-0" />}
    <div className={cn("max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed", m.role === "user" ? "bg-primary text-primary-foreground" : "card-surface")}>
      {m.content.replace(/\*\*(.+?)\*\*/g, "$1") || <Loader2 className="h-4 w-4 animate-spin" />}
    </div>
  </div>
);

const ConversationsSheet = ({ open, onOpenChange, onPick, activeId, onDeleted }) => {
  const { active } = useHousehold();
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["assistant-convs", active?.id], queryFn: () => api.get(`/assistant/conversations?household_id=${active.id}`).then((r) => r.data), enabled: !!active && open });
  const remove = async (id) => {
    try { await api.delete(`/assistant/conversations/${id}`); qc.invalidateQueries({ queryKey: ["assistant-convs"] }); qc.invalidateQueries({ queryKey: ["assistant-quota"] }); onDeleted(id); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[min(22rem,100vw)]" data-testid="assistant-history">
        <SheetHeader><SheetTitle className="font-heading">Conversations</SheetTitle></SheetHeader>
        <ul className="mt-4 space-y-1">
          {data.length === 0 && <li className="text-sm text-muted-foreground">Aucune conversation.</li>}
          {data.map((c) => (
            <li key={c.id} className={cn("flex items-center gap-2 rounded-xl px-3 py-2 text-sm hover:bg-secondary", c.id === activeId && "bg-secondary")}>
              <button className="min-w-0 flex-1 truncate text-left" onClick={() => onPick(c.id)} data-testid={`assistant-conv-${c.id}`}>{c.title}</button>
              <ConfirmDelete title="Supprimer cette conversation ?" testid={`assistant-conv-delete-${c.id}`} onConfirm={() => remove(c.id)} />
            </li>
          ))}
        </ul>
      </SheetContent>
    </Sheet>
  );
};

export default function Assistant() {
  const { user } = useAuth();
  const { active } = useHousehold();
  const qc = useQueryClient();
  const [msgs, setMsgs] = useState([]);
  const [cid, setCid] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [hist, setHist] = useState(false);
  const end = useRef(null);
  const quota = useQuery({ queryKey: ["assistant-quota"], queryFn: () => api.get("/assistant/quota").then((r) => r.data) });
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [msgs]);
  useEffect(() => { setMsgs([]); setCid(null); }, [active?.id]);

  const send = async (q) => {
    const message = (q ?? text).trim();
    if (!message || busy || !active) return;
    setText("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    const append = (s) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + s }; return c; });
    try {
      const post = () => fetch(`${API}/assistant/chat`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ household_id: active.id, message, conversation_id: cid }) });
      let res = await post();
      if (res.status === 401 && (await api.post("/auth/refresh").then(() => true).catch(() => false))) res = await post();
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "L'assistant est indisponible.");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        parts.forEach((p) => {
          if (!p.startsWith("data: ")) return;
          const ev = JSON.parse(p.slice(6));
          if (ev.type === "meta") setCid(ev.conversation_id);
          if (ev.type === "delta") append(ev.content);
          if (ev.type === "error") append(ev.message);
        });
      }
    } catch (e) {
      setMsgs((m) => m.slice(0, -2));
      setText(message);
      toast.error(typeof e.message === "string" ? e.message : formatApiError(e));
    } finally {
      setBusy(false);
      ["assistant-quota", "assistant-convs"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    }
  };
  const pick = async (id) => {
    setHist(false);
    try { setMsgs(await api.get(`/assistant/conversations/${id}/messages`).then((r) => r.data)); setCid(id); } catch (e) { toast.error(formatApiError(e)); }
  };
  const q = quota.data;
  const blocked = busy || user.read_only || q?.remaining === 0;
  return (
    <div className="mx-auto flex min-h-[70vh] max-w-2xl flex-col" data-testid="assistant-page">
      <div className="flex items-center justify-end gap-2">
        {q && <span className="text-xs text-muted-foreground" data-testid="assistant-quota">{q.remaining} message(s) restant(s) aujourd'hui</span>}
        <button onClick={() => { setMsgs([]); setCid(null); }} className="flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs hover:bg-secondary" data-testid="assistant-new"><MessageSquarePlus className="h-3.5 w-3.5" />Nouvelle</button>
        <button onClick={() => setHist(true)} className="flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs hover:bg-secondary" data-testid="assistant-history-button"><History className="h-3.5 w-3.5" />Historique</button>
      </div>
      {msgs.length === 0 ? (
        <div className="fade-up flex flex-1 flex-col items-center justify-center py-8 text-center">
          <div className="relative"><div className="absolute inset-0 -z-10 scale-150 rounded-full bg-mint/40 blur-3xl dark:bg-mint/10" /><CoinMark size={84} className="coin-float" /></div>
          <h1 className="mt-8 text-3xl font-extrabold sm:text-4xl">Bonjour {user.name.split(" ")[0]}, je suis votre copilote.</h1>
          <p className="mt-3 max-w-md text-sm text-muted-foreground sm:text-base">Budget, garanties, équipements, entretiens, assurances : je réponds à partir des données de {active?.name || "votre foyer"}. Je ne modifie rien.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-2">
            {SUGGESTIONS.map((s, i) => (
              <button key={s} onClick={() => send(s)} disabled={blocked} className="fade-up rounded-full border border-border bg-card px-4 py-2 text-sm transition-colors hover:border-sage hover:bg-mint/30 dark:hover:bg-mint/10"
                style={{ animationDelay: `${150 + i * 60}ms` }} data-testid={`assistant-suggestion-${i}`}>{s}</button>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex-1 space-y-4 py-6" data-testid="assistant-thread">{msgs.map((m, i) => <Bubble key={m.id || i} m={m} />)}<div ref={end} /></div>
      )}
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="card-surface sticky bottom-28 mt-4 flex items-center gap-2 bg-card p-2 md:bottom-6">
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} disabled={blocked} placeholder={user.read_only ? "Indisponible en lecture seule" : q?.remaining === 0 ? "Limite du jour atteinte, revenez demain" : "Posez votre question…"}
          className="min-w-0 flex-1 bg-transparent px-3 py-2 text-sm outline-none placeholder:text-muted-foreground" data-testid="assistant-input" />
        <button type="submit" disabled={blocked || !text.trim()} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-navy text-[#F8FAF8] disabled:opacity-50 dark:bg-mint dark:text-navy" aria-label="Envoyer" data-testid="assistant-send-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
        </button>
      </form>
      <p className="mt-2 text-center text-[11px] text-muted-foreground">L'assistant peut se tromper : vérifiez les informations importantes.</p>
      <ConversationsSheet open={hist} onOpenChange={setHist} onPick={pick} activeId={cid} onDeleted={(id) => { if (id === cid) { setMsgs([]); setCid(null); } }} />
    </div>
  );
}
